import { useMemo } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { fmt2 } from "@/lib/api";
import { fl, monthShort, shortIDR } from "@/lib/finI18n";

export function SalesTrend({ rows, lang }) {
  const data = useMemo(() => {
    const by = {};
    rows.forEach((r) => {
      const k = String(r.date || "").slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(k)) return;
      const a = (by[k] = by[k] || { k, dpp: 0, ppn: 0, total: 0 });
      a.dpp += Number(r.dpp || 0); a.ppn += Number(r.ppn || 0); a.total += Number(r.total || 0);
    });
    const keys = Object.keys(by).sort();
    if (keys.length > 1) {
      let [y, m] = keys[0].split("-").map(Number);
      let k = keys[0];
      while (k < keys[keys.length - 1]) {
        by[k] = by[k] || { k, dpp: 0, ppn: 0, total: 0 };
        m += 1;
        if (m > 12) { m = 1; y += 1; }
        k = `${y}-${String(m).padStart(2, "0")}`;
      }
    }
    return Object.values(by).sort((a, b) => a.k.localeCompare(b.k)).map((a) => ({ ...a, name: `${monthShort(Number(a.k.slice(5)), lang)} ${a.k.slice(2, 4)}` }));
  }, [rows, lang]);
  if (!data.length) return null;
  const sum = data.reduce((s, d) => s + d.dpp, 0);
  return (
    <div className="rounded-md border border-border p-3" data-testid="sales-trend">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-sm font-semibold">{fl("salesTrend", lang)}</p>
        <span className="num text-xs text-muted-foreground" data-testid="sales-trend-total">{fl("salesDpp", lang)}: {fmt2(sum)}</span>
      </div>
      <div className="h-64">
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={shortIDR} />
            <Tooltip formatter={(v) => fmt2(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="dpp" stackId="s" name={fl("salesDpp", lang)} fill="#2563eb" />
            <Bar dataKey="ppn" stackId="s" name={fl("ppn", lang)} fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="total" name={fl("total", lang)} stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
